/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { apiClient } from './apiClient';
import type {
  FormulaDto,
  FormulaDetailDto,
  FormulaListParams,
  TaxonomyCodeDto,
  TaxonomyWithCountDto,
  TaxonomyFormulaAssignmentDto,
  SuggestedTaxonomyDto,
  FilterOptionsDto,
  TaxonomyFilterOptionsDto,
  CoverageDto,
  AuditEntryDto,
  AuditFilterParams,
  ProgressDataPoint,
  ProductivityDto,
  UserPreferencesDto,
  AssignRequest,
  BatchAssignRequest,
  AssignmentResultDto,
  BatchAssignResultDto,
  HeartbeatResponseDto,
  ClaimBatchDto,
  ActiveClaimDto,
} from '@shared/types';

// ─── Formula ──────────────────────────────────────────────────────────────────
export function fetchFormulas(params: FormulaListParams & { _bust?: number }) {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('pageSize', String(params.pageSize));
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.dosageForm) qs.set('dosageForm', params.dosageForm);
  if (params.schedule) qs.set('schedule', params.schedule);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.search) qs.set('search', params.search);
  if (params.searchMode && params.searchMode !== 'both') qs.set('searchMode', params.searchMode);
  if (params._bust) qs.set('_t', String(params._bust));
  return apiClient.get<FormulaDto[]>(`/formulas?${qs.toString()}`);
}

export function fetchFormulaDetail(setId: string) {
  return apiClient.get<FormulaDetailDto>(`/formulas/${setId}`);
}

export function fetchFilterOptions() {
  return apiClient.get<FilterOptionsDto>('/formulas/filter-options');
}

/**
 * Fetch all formula set_ids matching the current filter set (across all pages).
 * Used by "select all matching results".
 */
export function fetchAllMatchingSetIds(params: Omit<FormulaListParams, 'page' | 'pageSize' | 'sortBy'>) {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.dosageForm) qs.set('dosageForm', params.dosageForm);
  if (params.schedule) qs.set('schedule', params.schedule);
  if (params.search) qs.set('search', params.search);
  if (params.searchMode && params.searchMode !== 'both') qs.set('searchMode', params.searchMode);
  return apiClient.get<string[]>(`/formulas/all-set-ids?${qs.toString()}`);
}

export function fetchNextUnassigned(afterName: string) {
  return apiClient.get<{ setId: string; name: string } | null>(
    `/formulas/next-unassigned?afterName=${encodeURIComponent(afterName)}`
  );
}

// ─── Taxonomy ─────────────────────────────────────────────────────────────────
export function searchTaxonomy(q: string) {
  return apiClient.get<TaxonomyCodeDto[]>(`/taxonomy/search?q=${encodeURIComponent(q)}`);
}

export function fetchAllTaxonomyCodes() {
  return apiClient.get<TaxonomyCodeDto[]>('/taxonomy/all');
}

export function fetchAllTaxonomyCodesWithCounts(opts?: { noCache?: boolean }) {
  const qs = opts?.noCache ? '?noCache=1' : '';
  return apiClient.get<TaxonomyWithCountDto[]>(`/taxonomy/all-with-counts${qs}`);
}

export function fetchSuggestedTaxonomies(formulaSetId: string) {
  return apiClient.get<SuggestedTaxonomyDto[]>(`/taxonomy/suggested?formulaSetId=${encodeURIComponent(formulaSetId)}`);
}

export function fetchTaxonomyFilterOptions() {
  return apiClient.get<TaxonomyFilterOptionsDto>('/taxonomy/filter-options');
}

export function fetchFormulasForTaxonomy(taxonomySetId: string) {
  return apiClient.get<TaxonomyFormulaAssignmentDto[]>(`/taxonomy/${encodeURIComponent(taxonomySetId)}/formulas`);
}

// ─── Assignment ───────────────────────────────────────────────────────────────
export function assignTaxonomy(body: AssignRequest) {
  return apiClient.post<AssignmentResultDto>('/assignments', body);
}

export function removeAssignment(xlinkSetId: string, reason: string) {
  return apiClient.del<void>(`/assignments/${xlinkSetId}?reason=${encodeURIComponent(reason)}`);
}

export function batchAssign(body: BatchAssignRequest) {
  return apiClient.post<BatchAssignResultDto>('/assignments/batch', body);
}

// ─── Session / Claims ─────────────────────────────────────────────────────────
export function sendHeartbeat() {
  return apiClient.post<HeartbeatResponseDto>('/sessions/heartbeat');
}

export function claimFormulas(count: number) {
  return apiClient.post<ClaimBatchDto>('/claims', { count });
}

export function fetchMyClaim() {
  return apiClient.get<ClaimBatchDto | null>('/claims/mine');
}

export function releaseClaim(batchId: string) {
  return apiClient.del<void>(`/claims/${batchId}`);
}

export function fetchActiveClaims() {
  return apiClient.get<ActiveClaimDto[]>('/claims');
}

export function forceReleaseClaim(batchId: string) {
  return apiClient.del<void>(`/claims/${batchId}/force`);
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
export function fetchCoverage() {
  return apiClient.get<CoverageDto>('/dashboard/coverage');
}

export function fetchAuditTrail(params: AuditFilterParams) {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('pageSize', String(params.pageSize));
  if (params.pharmacistOid) qs.set('pharmacistOid', params.pharmacistOid);
  if (params.formulaSetId) qs.set('formulaSetId', params.formulaSetId);
  if (params.actionType) qs.set('actionType', params.actionType);
  if (params.fromDate) qs.set('fromDate', params.fromDate);
  if (params.toDate) qs.set('toDate', params.toDate);
  return apiClient.get<AuditEntryDto[]>(`/dashboard/audit?${qs.toString()}`);
}

export function fetchProgress(days: number) {
  return apiClient.get<ProgressDataPoint[]>(`/dashboard/progress?days=${days}`);
}

export function fetchProductivity(days: number) {
  return apiClient.get<ProductivityDto[]>(`/dashboard/productivity?days=${days}`);
}

// ─── Preferences ──────────────────────────────────────────────────────────────
export function fetchPreferences() {
  return apiClient.get<UserPreferencesDto>('/preferences');
}

export function savePreferences(prefs: UserPreferencesDto) {
  return apiClient.put<UserPreferencesDto>('/preferences', prefs);
}
